import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GetFeatureExperimentsResponse } from '@blisspointmedia/bpm-types/dist/FeatureExperiments';
import { useAppSelector } from '@src/redux';
import * as UserRedux from '@src/redux/user';
import { awaitJSON, UsersLambdaFetch } from '@src/utils/fetch-utils';
import { equalsIgnoreCase } from '@src/utils/text-utils';

// The current user's own user_group membership(s), from the experiment_special_users / "User
// Groups" table — the same source useCanEditExperiments reads for FeatureExperiments edit access.
// For pages that need to gate a feature to specific groups (rather than admin/internal role),
// not for editing FeatureExperiments itself.
export const useUserGroups = (): string[] => {
  const email = useAppSelector(UserRedux.emailSelector);
  const isInternal = useAppSelector(UserRedux.isInternalSelector);
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);

  const { data: specialUsers } = useQuery({
    // Same key as useCanEditExperiments's query — shares its cache when both are mounted.
    queryKey: ['featureExperiments', 'specialUsers'],
    queryFn: () =>
      UsersLambdaFetch('/getFeatureExperiments')
        .then(res => awaitJSON<GetFeatureExperimentsResponse>(res))
        .then(d => d.special_users),
    enabled: isInternal && Boolean(email) // the endpoint 403s for non-internal roles
  });

  return useMemo(() => {
    // Admins are reported as an 'admin' group (not a stored user_group), available immediately.
    const adminGroups = isAdmin ? ['admin'] : [];
    if (!email || !specialUsers) {
      return adminGroups;
    }
    return [
      ...adminGroups,
      ...specialUsers
        .filter(s => equalsIgnoreCase(s.user, email))
        .map(s => (s.user_group ?? '').toLowerCase())
    ];
  }, [email, specialUsers, isAdmin]);
};
